import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../services/firebase";
import { Save, CheckCircle, Plus, Trash2, RotateCcw } from "lucide-react";

const DAYS = ["Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"];

const DEFAULT_SCHEDULE = {
	Mon: [
		{ time: "10:00", note: "" },
		{ time: "13:00", note: "" },
		{ time: "17:30", note: "" },
		{ time: "18:45", note: "" },
		{ time: "20:00", note: "" },
	],
	Tues: [
		{ time: "09:00", note: "PT only" },
		{ time: "10:00", note: "" },
		{ time: "17:30", note: "" },
		{ time: "18:45", note: "" },
		{ time: "20:00", note: "" },
	],
	Wed: [
		{ time: "10:00", note: "" },
		{ time: "11:00", note: "PT only" },
		{ time: "12:00", note: "" },
		{ time: "17:30", note: "" },
		{ time: "18:45", note: "" },
	],
	Thurs: [
		{ time: "10:00", note: "" },
		{ time: "17:30", note: "" },
		{ time: "18:45", note: "" },
		{ time: "20:00", note: "" },
	],
	Fri: [
		{ time: "11:00", note: "PT only" },
		{ time: "12:00", note: "" },
		{ time: "13:00", note: "" },
		{ time: "18:00", note: "" },
	],
	Sat: [
		{ time: "08:30", note: "" },
		{ time: "10:30", note: "" },
	],
};

const ScheduleManager = () => {
	const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [success, setSuccess] = useState("");
	const [error, setError] = useState("");
	const [newSlot, setNewSlot] = useState({ day: "Mon", time: "", note: "" });
	const [lastPublished, setLastPublished] = useState(null);

	// Fetch current schedule from Firestore
	useEffect(() => {
		const fetchSchedule = async () => {
			setLoading(true);
			try {
				const scheduleDoc = await getDoc(doc(db, "config", "schedule"));
				if (scheduleDoc.exists()) {
					const data = scheduleDoc.data();
					// Merge with defaults in case some days are missing
					const merged = { ...DEFAULT_SCHEDULE };
					DAYS.forEach((day) => {
						if (data[day]) {
							merged[day] = data[day];
						}
					});
					setSchedule(merged);
					if (data.lastPublished) {
						setLastPublished(
							data.lastPublished.toDate
								? data.lastPublished.toDate()
								: new Date(data.lastPublished),
						);
					}
				}
			} catch (err) {
				setError("Failed to load schedule.");
				console.error("Error loading schedule:", err);
			}
			setLoading(false);
		};
		fetchSchedule();
	}, []);

	const handleAddSlot = (e) => {
		e.preventDefault();
		if (!newSlot.time.trim()) {
			setError("Please enter a time.");
			return;
		}

		setError("");
		setSchedule((prev) => ({
			...prev,
			[newSlot.day]: [
				...prev[newSlot.day],
				{ time: newSlot.time.trim(), note: newSlot.note.trim() },
			].sort((a, b) => a.time.localeCompare(b.time)),
		}));
		setNewSlot({ day: newSlot.day, time: "", note: "" });
	};

	const handleRemoveSlot = (day, index) => {
		setSchedule((prev) => ({
			...prev,
			[day]: prev[day].filter((_, i) => i !== index),
		}));
	};

	const handlePublish = async () => {
		setError("");
		setSuccess("");
		setSaving(true);

		try {
			const dataToSave = { ...schedule, lastPublished: serverTimestamp() };
			await setDoc(doc(db, "config", "schedule"), dataToSave);
			setLastPublished(new Date());
			setSuccess("Schedule published successfully! Changes are now live.");
			setTimeout(() => setSuccess(""), 4000);
		} catch (err) {
			setError("Failed to publish schedule. Please try again.");
			console.error("Error saving schedule:", err);
		}
		setSaving(false);
	};

	const handleReset = async () => {
		setError("");
		setLoading(true);
		try {
			const scheduleDoc = await getDoc(doc(db, "config", "schedule"));
			if (scheduleDoc.exists()) {
				const data = scheduleDoc.data();
				const merged = { ...DEFAULT_SCHEDULE };
				DAYS.forEach((day) => {
					if (data[day]) {
						merged[day] = data[day];
					}
				});
				setSchedule(merged);
			} else {
				setSchedule(DEFAULT_SCHEDULE);
			}
		} catch (err) {
			setError("Failed to reload schedule.");
		}
		setLoading(false);
	};

	if (loading) {
		return (
			<div className="text-center py-5">
				<div className="spinner-border text-primary" role="status">
					<span className="visually-hidden">Loading...</span>
				</div>
			</div>
		);
	}

	return (
		<div>
			<p className="text-muted mb-3">
				Manage the public session timetable. Add or remove time slots and
				publish changes to the website.
			</p>

			{error && (
				<div className="alert alert-danger" role="alert">
					{error}
				</div>
			)}
			{success && (
				<div
					className="alert alert-success d-flex align-items-center"
					role="alert"
				>
					<CheckCircle size={18} className="me-2" />
					{success}
				</div>
			)}

			{lastPublished && (
				<p className="text-muted small mb-3">
					Last published:{" "}
					{lastPublished.toLocaleDateString("en-GB", {
						day: "numeric",
						month: "short",
						year: "numeric",
						hour: "2-digit",
						minute: "2-digit",
					})}
				</p>
			)}

			{/* Add Slot Form */}
			<div
				className="p-3 mb-4 rounded"
				style={{
					backgroundColor: "rgba(255,255,255,0.05)",
					border: "1px solid #333",
				}}
			>
				<h6 className="mb-3">
					<Plus size={16} className="me-1" />
					Add Time Slot
				</h6>
				<form onSubmit={handleAddSlot}>
					<div className="row g-2 align-items-end">
						<div className="col-auto">
							<label className="form-label small">Day</label>
							<select
								className="form-select form-select-sm"
								value={newSlot.day}
								onChange={(e) =>
									setNewSlot({ ...newSlot, day: e.target.value })
								}
							>
								{DAYS.map((day) => (
									<option key={day} value={day}>
										{day}
									</option>
								))}
							</select>
						</div>
						<div className="col-auto">
							<label className="form-label small">Time</label>
							<input
								type="time"
								className="form-control form-control-sm"
								value={newSlot.time}
								onChange={(e) =>
									setNewSlot({ ...newSlot, time: e.target.value })
								}
								required
							/>
						</div>
						<div className="col-auto">
							<label className="form-label small">Note (optional)</label>
							<input
								type="text"
								className="form-control form-control-sm"
								value={newSlot.note}
								onChange={(e) =>
									setNewSlot({ ...newSlot, note: e.target.value })
								}
								placeholder="e.g. PT only"
								style={{ maxWidth: "140px" }}
							/>
						</div>
						<div className="col-auto">
							<button type="submit" className="btn btn-primary btn-sm">
								<Plus size={14} className="me-1" />
								Add
							</button>
						</div>
					</div>
				</form>
			</div>

			{/* Schedule Grid */}
			<div className="table-responsive mb-4">
				<table className="table table-sm table-bordered text-center align-middle">
					<thead>
						<tr>
							{DAYS.map((day) => (
								<th key={day} style={{ minWidth: "100px" }}>
									{day}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{/* Build rows by hour so same-hour slots share a row */}
						{(() => {
							const allTimes = [
								...new Set(
									DAYS.flatMap((day) => schedule[day].map((slot) => slot.time)),
								),
							].sort();
							const hours = [
								...new Set(allTimes.map((t) => t.split(":")[0])),
							].sort();
							return hours.map((hour) => (
								<tr key={hour}>
									{DAYS.map((day) => {
										const slotIndex = schedule[day].findIndex(
											(s) => s.time.split(":")[0] === hour,
										);
										if (slotIndex === -1) return <td key={day}></td>;
										const slot = schedule[day][slotIndex];
										return (
											<td key={day}>
												<div className="d-flex align-items-center justify-content-center gap-1">
													<span>
														{formatTime(slot.time)}
														{slot.note && (
															<span className="text-muted small">*</span>
														)}
													</span>
													<button
														className="btn btn-link btn-sm text-danger p-0"
														onClick={() => handleRemoveSlot(day, slotIndex)}
														title={`Remove ${slot.time} on ${day}`}
														aria-label={`Remove ${slot.time} on ${day}`}
													>
														<Trash2 size={12} />
													</button>
												</div>
												{slot.note && (
													<small
														className="text-muted d-block"
														style={{ fontSize: "0.7rem" }}
													>
														{slot.note}
													</small>
												)}
											</td>
										);
									})}
								</tr>
							));
						})()}
					</tbody>
				</table>
			</div>

			{/* Action Buttons */}
			<div className="d-flex gap-2 flex-wrap">
				<button
					className="btn btn-success"
					onClick={handlePublish}
					disabled={saving}
				>
					{saving ? (
						"Publishing..."
					) : (
						<>
							<Save size={16} className="me-1" />
							Publish Schedule
						</>
					)}
				</button>
				<button
					className="btn btn-outline-secondary"
					onClick={handleReset}
					disabled={saving}
				>
					<RotateCcw size={16} className="me-1" />
					Discard Changes
				</button>
			</div>
		</div>
	);
};

// Format 24hr time string (HH:MM) to display format
function formatTime(timeStr) {
	if (!timeStr) return "";
	const [hours, minutes] = timeStr.split(":");
	const h = parseInt(hours, 10);
	const m = minutes || "00";
	return `${String(h).padStart(2, "0")}:${m}`;
}

export default ScheduleManager;
